/**
 * SeasonModeChip — サイドバー上部に常時表示する季節モードトグル (v3.2.23+)。
 *
 * 設計:
 *   - 現在のモード ('通常期' / 'セール期') をチップで表示
 *   - クリックで切替確認ダイアログ表示
 *   - 切替時、全ブランドの商品カタログ・報酬セットが effective レイヤで一斉切替
 *   - SA (level='external') には表示しない (オペは社内のみ)
 *   - owner または editor (sales.settings 編集可能) のみ操作可能 (それ以外は read-only)
 */
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles, Tag } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import {
  useSalesSettingsWithDefaults,
  useUpdateSalesSettings,
} from '@/hooks/useSettings';
import { cn } from '@/lib/utils';

interface Props {
  collapsed: boolean;
}

export function SeasonModeChip({ collapsed }: Props) {
  const { user } = useAuth();
  const { merged, isLoading } = useSalesSettingsWithDefaults();
  const update = useUpdateSalesSettings();
  const qc = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const current: 'normal' | 'sale' =
    merged.seasonMode === 'sale' ? 'sale' : 'normal';
  const target: 'normal' | 'sale' = current === 'normal' ? 'sale' : 'normal';

  // SA は非表示 (社内オペレーション専用)
  if (user?.dept === 'sales' && user?.level === 'external') {
    return null;
  }
  // owner または社内 (editor 相当) のみ操作可。viewer も視認は可だがクリックで dialog は出さない
  const canEdit = user?.isOwner || user?.level !== 'external';
  if (isLoading || !user) return null;

  const handleConfirm = async () => {
    try {
      await update.mutateAsync({ seasonMode: target });
      // season-aware な catalog 系 query を全て invalidate
      qc.invalidateQueries({ queryKey: ['product_catalogs'] });
      qc.invalidateQueries({ queryKey: ['commission_sets'] });
      toast.success(
        `季節モードを「${target === 'sale' ? 'セール期' : '通常期'}」に切替しました`,
      );
      setDialogOpen(false);
    } catch (err) {
      toast.error(
        `失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => canEdit && setDialogOpen(true)}
        disabled={!canEdit}
        className={cn(
          'flex items-center gap-2 mx-3 my-2 px-2.5 py-2 rounded-md border-2 transition-colors',
          current === 'sale'
            ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200'
            : 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]',
          canEdit
            ? 'hover:opacity-80 cursor-pointer'
            : 'opacity-70 cursor-default',
          collapsed && 'justify-center px-1',
        )}
        title={
          collapsed
            ? `季節: ${current === 'sale' ? 'セール期' : '通常期'}${canEdit ? ' (クリックで切替)' : ''}`
            : undefined
        }
      >
        {current === 'sale' ? (
          <Sparkles className="size-4 shrink-0" />
        ) : (
          <Tag className="size-4 shrink-0" />
        )}
        {!collapsed && (
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[9px] uppercase tracking-wider opacity-70 leading-none">
              季節モード
            </div>
            <div className="text-xs font-semibold leading-tight mt-0.5">
              {current === 'sale' ? 'セール期' : '通常期'}
              {canEdit && (
                <span className="ml-1 text-[10px] font-normal opacity-60">
                  ▾
                </span>
              )}
            </div>
          </div>
        )}
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              季節モードを「{target === 'sale' ? 'セール期' : '通常期'}」に切替しますか？
            </DialogTitle>
            <DialogDescription>
              切替すると、以下が即座に変わります：
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-2 text-sm text-[var(--text-secondary)] py-2 pl-4 list-disc">
            <li>
              <strong>商品カタログ</strong>:
              各ブランドの「isActive=true かつ season=
              <span className="text-[var(--accent)] font-mono">
                {target}
              </span>
              」のカタログが effective になります（営業フォーム、見積等で参照）
            </li>
            <li>
              <strong>報酬セット</strong>:
              effective カタログの linkedCommissionSetId が指す報酬セットに切替（入金時の報酬計算に適用）
            </li>
            <li>
              <strong>SA 報酬表</strong>: 「適用中」マークが
              <span className="text-[var(--accent)] font-mono">
                {target === 'sale' ? 'セール期' : '通常期'}
              </span>
              側に移動
            </li>
          </ul>

          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs text-amber-900 dark:text-amber-200">
            ⚠️ 既存の見積書・請求書・Deal の productCatalogSnapshot は影響を受けません（送信時点でスナップショット済み）。
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              disabled={update.isPending}
            >
              キャンセル
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={update.isPending}
            >
              {update.isPending
                ? '切替中…'
                : `${target === 'sale' ? 'セール期' : '通常期'} に切替`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
